import { formatOverlayTimestamp } from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

import { getFloatingPlacement } from './overlayPositioning'
import { RulerMetricsPanel } from './RulerMetricsPanel'
import {
  getSeriesInfoFromUPlot,
  projectRulerSpan,
  Ruler,
  RulerHoverState,
  RulerMetrics,
  RulerSelection,
  VisibleSensorInfo,
} from './useRulerTool'

/* ─── Styled components ─────────────────────────────────────────────────── */

const OverlayRoot = styled.div<{ $isActive: boolean; $resizing: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 15;
  cursor: ${({ $isActive, $resizing }) =>
    !$isActive ? 'default' : $resizing ? 'ew-resize' : 'crosshair'};
  pointer-events: ${({ $isActive }) => ($isActive ? 'all' : 'none')};
  user-select: none;
`

const CursorLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.55);
  pointer-events: none;
`

const HoverTooltip = styled.div`
  position: absolute;
  top: 8px;
  pointer-events: none;
  background: rgba(10, 10, 10, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  padding: 3px 7px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 9px;
  color: ${CHART_COLORS.TOOLTIP_TIME_COLOR};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  z-index: 20;
`

const SelectionBand = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(102, 126, 234, 0.12);
  border-left: 1px solid ${CHART_COLORS.CURSOR_ACCENT};
  border-right: 1px solid ${CHART_COLORS.CURSOR_ACCENT};
  pointer-events: none;
`

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  cursor: ew-resize;
  z-index: 25;
  pointer-events: all;
`

const MetricsPanelAnchor = styled.div`
  position: absolute;
  pointer-events: all;
  z-index: 20;
`

const DragTimeRangeLabel = styled.div`
  position: absolute;
  pointer-events: none;
  background: rgba(10, 10, 10, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 4px 10px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 11px;
  color: ${CHART_COLORS.TOOLTIP_TIME_COLOR};
  white-space: nowrap;
  z-index: 20;
  transform: translateX(-50%);
`

const HighLowMarker = styled.div<{ $color: string }>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
  background: ${({ $color }) => $color};
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 22;
`

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const OVERLAY_EDGE_PADDING = 8
const HOVER_TOOLTIP_GAP = 10
const PANEL_GAP = 8
const FALLBACK_TOOLTIP_WIDTH = 180
const FALLBACK_METRICS_PANEL_WIDTH = 295
const FALLBACK_DRAG_LABEL_WIDTH = 260

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function useObservedElementWidth<T extends HTMLElement>(
  element: T | null
): number {
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    if (!element) {
      setWidth(0)
      return
    }

    const updateWidth = () => {
      const nextWidth = element.offsetWidth
      setWidth((prevWidth) => (prevWidth === nextWidth ? prevWidth : nextWidth))
    }

    updateWidth()
    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [element])

  return width
}

/* ─── PlotBounds hook ────────────────────────────────────────────────────── */

interface PlotBounds {
  overLeft: number
  overRight: number
  overTop: number
  overBottom: number
}

function usePlotBounds(
  uPlotRef: React.RefObject<uPlot | null>,
  rootRef: React.RefObject<HTMLDivElement | null>
): PlotBounds | null {
  const [bounds, setBounds] = useState<PlotBounds | null>(null)

  useLayoutEffect(() => {
    let rafId: number | null = null
    const compute = () => {
      const u = uPlotRef.current
      const root = rootRef.current
      // uPlot is created in a passive effect (useUPlotChart) that runs after
      // this layout effect, and can be recreated later on a recreate-key
      // change. On the load path the overlay mounts as soon as a saved chart
      // restores rulers — which is typically before the (larger) time-series
      // fetch has built uPlot — so `u`/`u.over` is not ready on the first pass.
      // Nothing else recomputes bounds once uPlot arrives (the rootRef
      // ResizeObserver never fires for a uPlot mount), so retry on the next
      // frame until the plot area is measurable. Without this a loaded ruler
      // keeps bounds=null forever and never renders, even after a pan/zoom.
      if (!u || !root || !u.over) {
        rafId = requestAnimationFrame(compute)
        return
      }
      const rootRect = root.getBoundingClientRect()
      const overRect = u.over.getBoundingClientRect()
      setBounds({
        overLeft: overRect.left - rootRect.left,
        overRight: overRect.right - rootRect.left,
        overTop: overRect.top - rootRect.top,
        overBottom: overRect.bottom - rootRect.top,
      })
    }
    compute()
    const ro = new ResizeObserver(compute)
    if (rootRef.current) ro.observe(rootRef.current)
    return () => {
      ro.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [uPlotRef, rootRef])

  return bounds
}

/* ─── Main component ─────────────────────────────────────────────────────── */

interface RulerOverlayProps {
  uPlotRef: React.RefObject<uPlot | null>
  isActive: boolean
  /**
   * Draw the persisted rulers (bands, panels, high/low markers). False while a
   * view-changing load is in flight for the current window, so a saved ruler
   * appears at its final projected position once the data settles instead of
   * projecting against the preserved previous-window data and then jumping.
   * The live cursor/draft/interaction layer are unaffected.
   */
  showPersisted: boolean
  hoverState: RulerHoverState | null
  rulers: Ruler[]
  draft: RulerSelection | null
  isDragging: boolean
  resizingEdge: 'start' | 'end' | null
  onSetRulerSensorIds: (rulerId: string, ids: string[]) => void
  onRemoveRuler: (rulerId: string) => void
  onSetRulerMinimized: (rulerId: string, isMinimized: boolean) => void
  onSetRulerPanelOffsetY: (rulerId: string, offsetY: number) => void
  onUpdateRulerPixels: (rulerId: string, startPx: number, endPx: number) => void
  handleEdgeMouseDown: (
    rulerId: string,
    edge: 'start' | 'end',
    e: React.MouseEvent
  ) => void
  computeMetrics: (
    sensorId: string,
    startXValue: number,
    endXValue: number
  ) => RulerMetrics
  getVisibleSensors: () => VisibleSensorInfo[]
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave: () => void
}

export const RulerOverlay: React.FC<RulerOverlayProps> = ({
  uPlotRef,
  isActive,
  showPersisted,
  hoverState,
  rulers,
  draft,
  isDragging,
  resizingEdge,
  onSetRulerSensorIds,
  onRemoveRuler,
  onSetRulerMinimized,
  onSetRulerPanelOffsetY,
  onUpdateRulerPixels,
  handleEdgeMouseDown,
  computeMetrics,
  getVisibleSensors,
  onMouseMove,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>(
    null
  )
  const [dragLabelElement, setDragLabelElement] =
    useState<HTMLDivElement | null>(null)
  const plotBounds = usePlotBounds(uPlotRef, rootRef)
  const tooltipWidth =
    useObservedElementWidth(tooltipElement) || FALLBACK_TOOLTIP_WIDTH
  const dragLabelWidth =
    useObservedElementWidth(dragLabelElement) || FALLBACK_DRAG_LABEL_WIDTH

  // Stable refs so the uPlot scale hook never has stale closures
  const rulersRef = useRef(rulers)
  useEffect(() => {
    rulersRef.current = rulers
  }, [rulers])
  const plotBoundsRef = useRef(plotBounds)
  useEffect(() => {
    plotBoundsRef.current = plotBounds
  }, [plotBounds])
  const onUpdatePixelsRef = useRef(onUpdateRulerPixels)
  useEffect(() => {
    onUpdatePixelsRef.current = onUpdateRulerPixels
  }, [onUpdateRulerPixels])

  // Register a uPlot setScale hook (once) to react to zoom/pan on the x-axis
  useLayoutEffect(() => {
    const u = uPlotRef.current
    if (!u) return

    const handler = (_u: uPlot, key: string) => {
      if (key !== 'x') return
      const bounds = plotBoundsRef.current
      if (!bounds) return

      for (const r of rulersRef.current) {
        const startPx = _u.valToPos(r.startXValue, 'x') + bounds.overLeft
        const endPx = _u.valToPos(r.endXValue, 'x') + bounds.overLeft

        if (!isFiniteNumber(startPx) || !isFiniteNumber(endPx)) continue

        // Keep off-screen rulers in state so they reappear when the view returns.
        if (endPx < bounds.overLeft || startPx > bounds.overRight) continue

        // Keep stored pixel positions in sync so edge-resize crossover stays accurate.
        onUpdatePixelsRef.current(r.id, startPx, endPx)
      }
    }

    u.hooks.setScale = u.hooks.setScale ?? []
    u.hooks.setScale.push(handler)

    return () => {
      if (u.hooks.setScale) {
        u.hooks.setScale = u.hooks.setScale.filter((h) => h !== handler)
      }
    }
  }, [uPlotRef]) // register once; uses refs for live values

  const u = uPlotRef.current
  const visibleSensors = getVisibleSensors()
  const visibleSensorIds = useMemo(
    () => new Set(visibleSensors.map((sensor) => sensor.sensorId)),
    [visibleSensors]
  )

  /* Tooltip horizontal placement */
  const overlayWidth = rootRef.current?.getBoundingClientRect().width ?? 800
  const tooltipPlacement = hoverState
    ? getFloatingPlacement({
        anchorLeft: hoverState.xPixelOnOverlay,
        anchorRight: hoverState.xPixelOnOverlay,
        containerWidth: overlayWidth,
        floatingWidth: tooltipWidth,
        gap: HOVER_TOOLTIP_GAP,
        edgePadding: OVERLAY_EDGE_PADDING,
        preferredSide: 'right',
      })
    : null

  /* Plot-area clip for resize handles */
  const plotTop = plotBounds?.overTop ?? 0
  const plotBottom = plotBounds
    ? `calc(100% - ${plotBounds.overBottom}px)`
    : '0'
  const metricsPanelTop = plotBounds !== null ? plotBounds.overTop + 6 : 6

  /* Draft (in-progress drag) — pixels come straight from state since uPlot is live */
  const draftStartPx = draft?.startPixelOnOverlay
  const draftEndPx = draft?.endPixelOnOverlay
  const draftBandWidth =
    isFiniteNumber(draftStartPx) && isFiniteNumber(draftEndPx)
      ? Math.max(0, draftEndPx - draftStartPx)
      : 0
  const dragLabelPlacement =
    draft !== null && isFiniteNumber(draftStartPx) && isFiniteNumber(draftEndPx)
      ? getFloatingPlacement({
          anchorLeft: draftStartPx,
          anchorRight: draftEndPx,
          containerWidth: overlayWidth,
          floatingWidth: dragLabelWidth,
          gap: PANEL_GAP,
          edgePadding: OVERLAY_EDGE_PADDING,
          preferredSide: 'left',
        })
      : null

  /* Pre-compute rendered geometry for each ruler */
  const renderedRulers = useMemo(() => {
    return rulers.map((ruler) => {
      const visibleSelectedSensorIds = ruler.selectedSensorIds.filter((id) =>
        visibleSensorIds.has(id)
      )
      const hasVisibleSelectedSensors = visibleSelectedSensorIds.length > 0

      const { startPx, endPx, horizontallyVisible } = projectRulerSpan(
        ruler,
        u && plotBounds ? (value, scale) => u.valToPos(value, scale) : null,
        plotBounds
      )

      return {
        ruler,
        visibleSelectedSensorIds,
        hasVisibleSelectedSensors,
        startPx,
        endPx,
        bandWidth: Math.max(0, endPx - startPx),
        horizontallyVisible,
      }
    })
  }, [rulers, u, plotBounds, visibleSensorIds])

  /* High/low markers across all rulers × selected sensors */
  const markers: Array<{
    key: string
    xPx: number
    yPx: number
    color: string
  }> = []

  if (u && plotBounds) {
    const seriesInfo = getSeriesInfoFromUPlot(u)
    for (const {
      ruler,
      visibleSelectedSensorIds,
      hasVisibleSelectedSensors,
      horizontallyVisible,
    } of renderedRulers) {
      if (!hasVisibleSelectedSensors || !horizontallyVisible) continue

      for (const sensorId of visibleSelectedSensorIds) {
        const series = seriesInfo.find((s) => s.sensorId === sensorId)
        if (!series) continue

        const sensor = visibleSensors.find((s) => s.sensorId === sensorId)
        const color = sensor?.color ?? '#ffffff'

        const m = computeMetrics(sensorId, ruler.startXValue, ruler.endXValue)

        if (m.maxTime !== null && m.max !== null) {
          try {
            const xPx = u.valToPos(m.maxTime, 'x') + plotBounds.overLeft
            const yPx = u.valToPos(m.max, series.scale) + plotBounds.overTop
            if (isFiniteNumber(xPx) && isFiniteNumber(yPx)) {
              markers.push({
                key: `${ruler.id}-${sensorId}-high`,
                xPx,
                yPx,
                color,
              })
            }
          } catch {
            // valToPos can throw if value is out of range
          }
        }
        if (m.minTime !== null && m.min !== null) {
          try {
            const xPx = u.valToPos(m.minTime, 'x') + plotBounds.overLeft
            const yPx = u.valToPos(m.min, series.scale) + plotBounds.overTop
            if (isFiniteNumber(xPx) && isFiniteNumber(yPx)) {
              markers.push({
                key: `${ruler.id}-${sensorId}-low`,
                xPx,
                yPx,
                color,
              })
            }
          } catch {
            // valToPos can throw if value is out of range
          }
        }
      }
    }
  }

  return (
    <OverlayRoot
      ref={rootRef}
      $isActive={isActive}
      $resizing={resizingEdge !== null}
      onMouseMove={isActive ? onMouseMove : undefined}
      onMouseDown={isActive ? onMouseDown : undefined}
      onMouseUp={isActive ? onMouseUp : undefined}
      onMouseLeave={isActive ? onMouseLeave : undefined}>
      {/* Vertical cursor line — only when active */}
      {isActive && hoverState && !isDragging && (
        <>
          <CursorLine
            style={{
              left: hoverState.xPixelOnOverlay,
              top: plotBounds?.overTop ?? 0,
              bottom: plotBounds
                ? `calc(100% - ${plotBounds.overBottom}px)`
                : 0,
            }}
          />
          <HoverTooltip
            ref={setTooltipElement}
            style={{ left: tooltipPlacement?.left ?? OVERLAY_EDGE_PADDING }}>
            {formatOverlayTimestamp(hoverState.xValue)}
          </HoverTooltip>
        </>
      )}

      {/* Draft band — visible while user is dragging out a new ruler */}
      {draft && isFiniteNumber(draftStartPx) && isFiniteNumber(draftEndPx) && (
        <SelectionBand
          data-testid='ruler-draft-band'
          style={{
            left: draftStartPx,
            width: draftBandWidth,
            top: plotTop,
            bottom: plotBottom,
          }}
        />
      )}

      {/* Drag time-range label — while drafting */}
      {isActive && draft && (
        <DragTimeRangeLabel
          ref={setDragLabelElement}
          style={{
            left: dragLabelPlacement?.left ?? OVERLAY_EDGE_PADDING,
            top: metricsPanelTop,
            transform: 'none',
          }}>
          {formatOverlayTimestamp(draft.startXValue)}
          {' — '}
          {formatOverlayTimestamp(draft.endXValue)}
        </DragTimeRangeLabel>
      )}

      {/* Persisted rulers */}
      {showPersisted &&
        renderedRulers.map(
          ({
            ruler,
            visibleSelectedSensorIds,
            hasVisibleSelectedSensors,
            startPx,
            endPx,
            bandWidth,
            horizontallyVisible,
          }) => {
            if (!horizontallyVisible) return null
            const shouldRender = hasVisibleSelectedSensors

            return (
              <RulerInstance
                key={ruler.id}
                ruler={ruler}
                startPx={startPx}
                endPx={endPx}
                bandWidth={bandWidth}
                plotTop={plotTop}
                plotBottom={plotBottom}
                metricsPanelTop={metricsPanelTop}
                overlayWidth={overlayWidth}
                isActive={isActive}
                shouldRender={shouldRender}
                visibleSensors={visibleSensors}
                visibleSelectedSensorIds={visibleSelectedSensorIds}
                visibleSensorIds={visibleSensorIds}
                onSetRulerSensorIds={onSetRulerSensorIds}
                onRemoveRuler={onRemoveRuler}
                onSetRulerMinimized={onSetRulerMinimized}
                onSetRulerPanelOffsetY={onSetRulerPanelOffsetY}
                handleEdgeMouseDown={handleEdgeMouseDown}
                computeMetrics={computeMetrics}
              />
            )
          }
        )}

      {/* High/low markers across all rulers */}
      {showPersisted &&
        markers.map(({ key, xPx, yPx, color }) => (
          <HighLowMarker
            key={key}
            $color={color}
            style={{ left: xPx, top: yPx }}
          />
        ))}
    </OverlayRoot>
  )
}

/* ─── Per-ruler instance ─────────────────────────────────────────────────── */

interface RulerInstanceProps {
  ruler: Ruler
  startPx: number
  endPx: number
  bandWidth: number
  plotTop: number
  plotBottom: number | string
  metricsPanelTop: number
  overlayWidth: number
  isActive: boolean
  shouldRender: boolean
  visibleSensors: VisibleSensorInfo[]
  visibleSelectedSensorIds: string[]
  visibleSensorIds: Set<string>
  onSetRulerSensorIds: (rulerId: string, ids: string[]) => void
  onRemoveRuler: (rulerId: string) => void
  onSetRulerMinimized: (rulerId: string, isMinimized: boolean) => void
  onSetRulerPanelOffsetY: (rulerId: string, offsetY: number) => void
  handleEdgeMouseDown: (
    rulerId: string,
    edge: 'start' | 'end',
    e: React.MouseEvent
  ) => void
  computeMetrics: (
    sensorId: string,
    startXValue: number,
    endXValue: number
  ) => RulerMetrics
}

const RulerInstance: React.FC<RulerInstanceProps> = ({
  ruler,
  startPx,
  endPx,
  bandWidth,
  plotTop,
  plotBottom,
  metricsPanelTop,
  overlayWidth,
  isActive,
  shouldRender,
  visibleSensors,
  visibleSelectedSensorIds,
  visibleSensorIds,
  onSetRulerSensorIds,
  onRemoveRuler,
  onSetRulerMinimized,
  onSetRulerPanelOffsetY,
  handleEdgeMouseDown,
  computeMetrics,
}) => {
  const [panelAnchorElement, setPanelAnchorElement] =
    useState<HTMLDivElement | null>(null)
  const panelWidth =
    useObservedElementWidth(panelAnchorElement) || FALLBACK_METRICS_PANEL_WIDTH

  const [dragState, setDragState] = useState<{
    startClientY: number
    startOffsetY: number
  } | null>(null)

  const handleHeaderMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setDragState({
        startClientY: event.clientY,
        startOffsetY: ruler.panelOffsetY,
      })
    },
    [ruler.panelOffsetY]
  )

  useEffect(() => {
    if (!dragState) return
    const handleMove = (event: MouseEvent) => {
      const deltaY = event.clientY - dragState.startClientY
      onSetRulerPanelOffsetY(ruler.id, dragState.startOffsetY + deltaY)
    }
    const handleUp = () => setDragState(null)
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [dragState, onSetRulerPanelOffsetY, ruler.id])

  const panelPlacement = getFloatingPlacement({
    anchorLeft: startPx,
    anchorRight: endPx,
    containerWidth: overlayWidth,
    floatingWidth: panelWidth,
    gap: PANEL_GAP,
    edgePadding: OVERLAY_EDGE_PADDING,
    preferredSide: 'left',
  })

  const handleVisibleSetSensorIds = useCallback(
    (nextVisibleSensorIds: string[]) => {
      let nextVisibleIndex = 0
      const next: string[] = []

      for (const sensorId of ruler.selectedSensorIds) {
        if (visibleSensorIds.has(sensorId)) {
          if (nextVisibleIndex < nextVisibleSensorIds.length) {
            next.push(nextVisibleSensorIds[nextVisibleIndex])
            nextVisibleIndex += 1
          }
          continue
        }
        next.push(sensorId)
      }

      while (nextVisibleIndex < nextVisibleSensorIds.length) {
        next.push(nextVisibleSensorIds[nextVisibleIndex])
        nextVisibleIndex += 1
      }

      onSetRulerSensorIds(ruler.id, next)
    },
    [onSetRulerSensorIds, ruler.id, ruler.selectedSensorIds, visibleSensorIds]
  )

  return (
    <>
      {shouldRender && (
        <SelectionBand
          data-testid='ruler-selection-band'
          data-ruler-id={ruler.id}
          style={{
            left: startPx,
            width: bandWidth,
            top: plotTop,
            bottom: plotBottom,
          }}
        />
      )}

      {isActive && shouldRender && (
        <>
          <ResizeHandle
            style={{
              left: startPx - 5,
              top: plotTop,
              bottom: plotBottom,
            }}
            onMouseDown={(e) => handleEdgeMouseDown(ruler.id, 'start', e)}
          />
          <ResizeHandle
            style={{
              left: endPx - 5,
              top: plotTop,
              bottom: plotBottom,
            }}
            onMouseDown={(e) => handleEdgeMouseDown(ruler.id, 'end', e)}
          />
        </>
      )}

      {shouldRender && visibleSensors.length > 0 && (
        <MetricsPanelAnchor
          ref={setPanelAnchorElement}
          style={{
            left: panelPlacement.left,
            top: metricsPanelTop + ruler.panelOffsetY,
          }}
          onMouseDown={(e) => e.stopPropagation()}>
          <RulerMetricsPanel
            selection={ruler}
            visibleSensors={visibleSensors}
            selectedSensorIds={visibleSelectedSensorIds}
            onSetSensorIds={handleVisibleSetSensorIds}
            onDismiss={() => onRemoveRuler(ruler.id)}
            isMinimized={ruler.isMinimized}
            onToggleMinimized={() =>
              onSetRulerMinimized(ruler.id, !ruler.isMinimized)
            }
            onHeaderMouseDown={handleHeaderMouseDown}
            isDragging={dragState !== null}
            maxWidth={panelPlacement.maxWidth}
            computeMetrics={computeMetrics}
          />
        </MetricsPanelAnchor>
      )}
    </>
  )
}
